/**
 * Core screen capture: getDisplayMedia → grab one frame (high-DPI) → PNG data URL → cleanup.
 * Does not handle countdown, crop, or redaction; use hooks/components for those.
 */

export type DisplaySurface = "monitor" | "window" | "browser";

export interface CaptureOptions {
  /** Prefer this surface when the picker allows (browser may still show full picker). */
  displaySurface?: DisplaySurface;
  /** Scale canvas by devicePixelRatio so text/code stays sharp. Default true. */
  highDpi?: boolean;
}

/**
 * Request screen/window/tab, grab a single frame, return as PNG data URL.
 * Stops the stream immediately after capture so the browser's "Sharing" indicator goes away.
 */
export async function captureScreenFrame(options: CaptureOptions = {}): Promise<string> {
  const { displaySurface = "monitor", highDpi = true } = options;

  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      displaySurface,
      width: { ideal: 4096 },
      height: { ideal: 4096 },
      frameRate: { ideal: 1 },
    },
    audio: false,
    selfBrowserSurface: "exclude",
    systemAudio: "exclude",
  });

  try {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error("No video track");

    const capture = track.getSettings();
    const width = capture.width ?? 1920;
    const height = capture.height ?? 1080;

    const video = document.createElement("video");
    video.srcObject = stream;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.play().then(resolve).catch(reject);
      };
      video.onerror = () => reject(new Error("Video load failed"));
    });

    const dpr = highDpi ? Math.min(window.devicePixelRatio || 1, 3) : 1;
    const canvas = document.createElement("canvas");
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2d context not available");
    ctx.scale(dpr, dpr);
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/png");

    track.stop();
    stream.getTracks().forEach((t) => t.stop());

    return dataUrl;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

/** Build metadata to attach to screen-capture messages (URL, resolution, userAgent). */
export function getScreenCaptureMeta(): {
  source: "screen-capture";
  url?: string;
  resolution?: string;
  userAgent?: string;
} {
  return {
    source: "screen-capture",
    url: typeof window !== "undefined" ? window.location.href : undefined,
    resolution:
      typeof window !== "undefined"
        ? `${window.screen.width}x${window.screen.height}`
        : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  };
}
